import type { FindingOut, RunFindingsFacetsOut } from '@/api/types';

/** Fixed review order / whitelist for the expense-source facet. The
 *  server facet is intersected with this so ordering + the closed
 *  vocabulary are preserved regardless of what the query returns. */
export const EXPENSE_SOURCE_ORDER = ['dext', 'brex', 'ramp', 'direct'] as const;

/** Fixed review order / whitelist for the ledger-source facet. */
export const LEDGER_SOURCE_ORDER = ['xero', 'qbo', 'netsuite'] as const;

/** Distinct sorted string values of one evidence key over the findings. */
function distinctEvidence(findings: FindingOut[], key: string): string[] {
  const set = new Set<string>();
  for (const f of findings) {
    const v = f.evidence[key];
    if (typeof v === 'string' && v.length > 0) set.add(v);
  }
  return Array.from(set).sort();
}

/** Distinct sorted values of a direct string field over the findings. */
function distinctField(findings: FindingOut[], pick: (f: FindingOut) => string): string[] {
  const set = new Set<string>();
  for (const f of findings) {
    const v = pick(f);
    if (typeof v === 'string' && v.length > 0) set.add(v);
  }
  return Array.from(set).sort();
}

/** Intersect a facet list with a fixed order array — keeps the whitelist
 *  vocabulary and stable ordering (used for expense/ledger sources). */
function inFixedOrder(order: readonly string[], present: readonly string[]): string[] {
  const set = new Set(present);
  return order.filter((s) => set.has(s));
}

export interface FindingFacetOptions {
  ruleIds: string[];
  provinces: string[];
  suppliers: string[];
  transactionTypes: string[];
  currencies: string[];
  sources: string[];
  ledgerSources: string[];
}

/** Build the filter-dropdown option lists. When the server facets have
 *  loaded they are the source of truth (honest under pagination — the
 *  loaded finding pages only cover a slice of the run); otherwise fall
 *  back to enumerating the loaded findings client-side. Expense + ledger
 *  sources are intersected with their fixed order arrays either way so
 *  the whitelist/order contract holds.
 *
 *  Supplier sort case-insensitive to match the pre-facet behaviour; the
 *  server already sorts, so re-sorting is a cheap no-op that guarantees
 *  parity with the client fallback. */
export function buildFacetOptions(
  facets: RunFindingsFacetsOut | undefined,
  findings: FindingOut[],
): FindingFacetOptions {
  if (facets) {
    return {
      ruleIds: facets.rule_ids,
      provinces: facets.provinces,
      suppliers: facets.suppliers,
      transactionTypes: facets.transaction_types,
      currencies: facets.currencies,
      sources: inFixedOrder(EXPENSE_SOURCE_ORDER, facets.expense_sources),
      ledgerSources: inFixedOrder(LEDGER_SOURCE_ORDER, facets.ledger_sources),
    };
  }
  return {
    ruleIds: distinctField(findings, (f) => f.rule_id),
    provinces: distinctEvidence(findings, 'supplier.province'),
    suppliers: distinctEvidence(findings, 'supplier.name').sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase()),
    ),
    transactionTypes: distinctEvidence(findings, 'transaction_type'),
    currencies: distinctField(findings, (f) => f.recovery.currency),
    sources: inFixedOrder(
      EXPENSE_SOURCE_ORDER,
      Array.from(new Set(findings.map((f) => f.expense_source))),
    ),
    ledgerSources: inFixedOrder(
      LEDGER_SOURCE_ORDER,
      Array.from(new Set(findings.map((f) => f.ledger_source))),
    ),
  };
}
